'use client';

import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import logPOISelected from '@alltrails/amplitude/events/logPOISelected';
import logTrailDetailsPOIListViewed from '@alltrails/amplitude/events/logTrailDetailsPOIListViewed';
import logTrailDetailsSeeAllClicked from '@alltrails/amplitude/events/logTrailDetailsSeeAllClicked';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Button from '@alltrails/denali/components/Button';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import { usePOIProfilePhoto } from '@alltrails/explore-service/hooks/usePOIProfilePhoto';
import getPOIIcon from '@alltrails/search/utils/getPOIIcon';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import TrailPOI from '@/utils/TrailPOI';
import styles from './TopSights.module.scss';

const thumbnailSize = 48;

const POIListItem = ({
  isBot,
  languageRegionCode,
  poi,
  trailId
}: {
  isBot?: boolean;
  languageRegionCode: LanguageRegionCode;
  poi: TrailPOI;
  trailId: number;
}) => {
  const [renderPhoto, setRenderPhoto] = useState(Boolean(poi.profilePhoto));
  const profilePhotoSrc = usePOIProfilePhoto({ poiId: poi.id, height: thumbnailSize * 2, width: thumbnailSize * 2 });

  let thumbnail = <div />;
  if (renderPhoto) {
    thumbnail = <Image alt={poi.name} className={styles.image} fill isLcp onError={() => setRenderPhoto(false)} src={profilePhotoSrc} />;
  } else {
    const IconComponent = getPOIIcon(poi.poiType, true /* filled */);
    thumbnail = <IconComponent color="--color-neutral-white" size="md" />;
  }

  return (
    <a
      className={styles.poi}
      href={wrapUrlSafe(`/poi/${poi.slug ?? poi.id}`, languageRegionCode)}
      onClick={() => {
        logPOISelected({
          poi_id: poi.id,
          poi_location: 'trail page',
          poi_detailed_location: 'trail card route card',
          trail_id: String(trailId)
        });
      }}
    >
      <div className={styles.thumbnail}>{thumbnail}</div>
      <div className={styles.text}>
        <Typography className={!isBot ? styles.truncated : undefined} component="div" variant="text200bold">
          {poi.name}
        </Typography>
        {poi.poiTypeLabel ? (
          <Typography className={!isBot ? styles.truncated : undefined} color="secondary" component="div" variant="text100">
            {poi.poiTypeLabel}
          </Typography>
        ) : null}
      </div>
      <ChevronSm className={styles.chevron} color="--color-text-secondary" orientation="right" />
    </a>
  );
};

type ExpandableTopSightsProps = {
  className?: string;
  isBot?: boolean;
  pois: TrailPOI[];
  trailId: number;
};

const ExpandableTopSights = ({ className, isBot, pois, trailId }: ExpandableTopSightsProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const [isExpanded, setIsExpanded] = useState(isBot);
  const canExpand = pois.length > 3;

  const container = useRef<HTMLDivElement>(null);
  const hasLoggedViewEvent = useRef(false);
  const hasLoggedSeeAllEvent = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasLoggedViewEvent.current) {
          hasLoggedViewEvent.current = true;
          logTrailDetailsPOIListViewed({ num_pois: pois?.length, trail_id: trailId });
        }
      });
    });

    if (container.current) {
      observer.observe(container.current);
    }

    return () => observer.disconnect();
  }, [pois?.length, trailId]);

  if (pois?.length) {
    const maxPois = isBot ? pois.length : 6;
    const visiblePOIs = isBot || isExpanded ? pois.slice(0, maxPois) : pois.slice(0, 3);
    return (
      <div className={classNames(styles.container, className)} ref={container}>
        <div className={styles.header}>
          <FormattedMessage defaultMessage="Top sights" />
        </div>
        {visiblePOIs.map(poi => (
          <POIListItem key={poi.id} isBot={isBot} languageRegionCode={languageRegionCode} poi={poi} trailId={trailId} />
        ))}
        {!isBot && canExpand && (
          <Button
            className={styles.expandButton}
            onClick={() => {
              if (!hasLoggedSeeAllEvent.current && !isExpanded) {
                hasLoggedSeeAllEvent.current = true;
                logTrailDetailsSeeAllClicked({ location: 'pois', trail_id: trailId });
              }
              container.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              setIsExpanded(prev => !prev);
            }}
            text={isExpanded ? <FormattedMessage defaultMessage="Show less" /> : <FormattedMessage defaultMessage="Show more" />}
          ></Button>
        )}
      </div>
    );
  }
  return null;
};

export default ExpandableTopSights;
